import React from 'react';
import {Text, View, Link, StyleSheet} from '@react-pdf/renderer';
import {remark} from 'remark';
import remarkParse from 'remark-parse';

const styles = StyleSheet.create({
  paragraph: {
    marginBottom: 8,
    fontSize: 11,
    lineHeight: 1.5
  },
  heading1: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
    marginTop: 12
  },
  heading2: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 6,
    marginTop: 10
  },
  heading3: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 6,
    marginTop: 8
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 4,
    fontSize: 11
  },
  listBullet: {
    width: 15,
    fontSize: 11
  },
  listContent: {
    flex: 1,
    fontSize: 11
  },
  bold: {
    fontFamily: 'Helvetica-Bold'
  },
  italic: {
    fontFamily: 'Helvetica-Oblique'
  },
  code: {
    fontFamily: 'Courier',
    fontSize: 10,
    backgroundColor: '#f5f5f5',
    padding: 2
  },
  codeBlock: {
    fontFamily: 'Courier',
    fontSize: 10,
    backgroundColor: '#f5f5f5',
    padding: 8,
    marginBottom: 8,
    borderRadius: 4
  },
  link: {
    color: '#2196f3',
    textDecoration: 'underline'
  },
  blockquote: {
    borderLeftWidth: 3,
    borderLeftColor: '#e0e0e0',
    paddingLeft: 12,
    marginBottom: 8,
    fontStyle: 'italic'
  }
});

interface MarkdownPDFProps {
  children: string;
  style?: any;
}

export function MarkdownPDF({children, style}: MarkdownPDFProps) {
  if (!children) {
    return null;
  }

  // Parse markdown to AST
  const ast = remark().use(remarkParse).parse(children);

  // Render AST nodes to PDF components
  const renderNode = (node: any, index: number): React.ReactNode => {
    switch (node.type) {
      case 'root':
        return (
          <View key={index} style={style}>
            {node.children?.map((child: any, i: number) => renderNode(child, i))}
          </View>
        );

      case 'paragraph':
        return (
          <Text key={index} style={styles.paragraph}>
            {node.children?.map((child: any, i: number) => renderInlineNode(child, i))}
          </Text>
        );

      case 'heading':
        const headingStyle = node.depth === 1 ? styles.heading1 : node.depth === 2 ? styles.heading2 : styles.heading3;
        return (
          <Text key={index} style={headingStyle}>
            {node.children?.map((child: any, i: number) => renderInlineNode(child, i))}
          </Text>
        );

      case 'list':
        return (
          <View key={index} style={{marginBottom: 8}}>
            {node.children?.map((child: any, i: number) => {
              const bullet = node.ordered ? `${i + 1}.` : '•';
              return (
                <View key={i} style={styles.listItem}>
                  <Text style={styles.listBullet}>{bullet}</Text>
                  <Text style={styles.listContent}>
                    {child.children?.map((c: any, j: number) => {
                      if (c.type === 'paragraph') {
                        return c.children?.map((inlineNode: any, k: number) => renderInlineNode(inlineNode, k));
                      }
                      return renderNode(c, j);
                    })}
                  </Text>
                </View>
              );
            })}
          </View>
        );

      case 'code':
        return (
          <Text key={index} style={styles.codeBlock}>
            {node.value}
          </Text>
        );

      case 'blockquote':
        return (
          <View key={index} style={styles.blockquote}>
            {node.children?.map((child: any, i: number) => renderNode(child, i))}
          </View>
        );

      case 'thematicBreak':
        return (
          <View
            key={index}
            style={{
              borderBottomWidth: 1,
              borderBottomColor: '#e0e0e0',
              marginVertical: 8
            }}
          />
        );

      default:
        // For unknown node types, try to render children if they exist
        if (node.children) {
          return node.children.map((child: any, i: number) => renderNode(child, i));
        }
        return null;
    }
  };

  // Render inline nodes (text, bold, italic, links, etc.)
  const renderInlineNode = (node: any, index: number): React.ReactNode => {
    switch (node.type) {
      case 'text':
        return node.value;

      case 'strong':
        return (
          <Text key={index} style={styles.bold}>
            {node.children?.map((child: any, i: number) => renderInlineNode(child, i))}
          </Text>
        );

      case 'emphasis':
        return (
          <Text key={index} style={styles.italic}>
            {node.children?.map((child: any, i: number) => renderInlineNode(child, i))}
          </Text>
        );

      case 'inlineCode':
        return (
          <Text key={index} style={styles.code}>
            {node.value}
          </Text>
        );

      case 'link':
        return (
          <Link key={index} src={node.url} style={styles.link}>
            {node.children?.map((child: any, i: number) => renderInlineNode(child, i))}
          </Link>
        );

      case 'break':
        return '\n';

      default:
        // For unknown inline types, try to render children if they exist
        if (node.children) {
          return node.children.map((child: any, i: number) => renderInlineNode(child, i));
        }
        return null;
    }
  };

  return <>{renderNode(ast, 0)}</>;
}